/**
 * Pure helpers behind the search box of the playlist song list (SongList.tsx). Kept free of React
 * and of the generated query types so they stay trivially unit-testable: the structural types below
 * are the subset of a playlist content item the search looks at, and both CurationSong and
 * CurationSongInput rows satisfy them as-is.
 */

export type SearchableSongInput = {
  __typename: 'CurationSongInput';
  id: string;
  title?: string | null;
  artist?: string | null;
  album?: string | null;
  songInputIsrc?: string | null;
};

export type SearchableSong = {
  __typename: 'CurationSong';
  id: string;
  isrc?: string | null;
  title?: string | null;
  artist?: string | null;
  album?: string | null;
  displayTitle?: string | null;
  displayArtist?: string | null;
  songInput?: { id: string } | null;
};

export type SearchableContentItem = SearchableSongInput | SearchableSong;

const compactStrings = (values: Array<string | null | undefined>): string[] =>
  values.filter((value): value is string => typeof value === 'string' && value.length > 0);

/** The IDs a playlist row can be looked up by: song ID, song input ID and ISRC. */
export function getSongSearchIds(song: SearchableContentItem): string[] {
  if (song.__typename === 'CurationSongInput') {
    return compactStrings([song.id, song.songInputIsrc]);
  }
  return compactStrings([song.id, song.songInput?.id, song.isrc]);
}

function getSongSearchTexts(song: SearchableContentItem): string[] {
  if (song.__typename === 'CurationSongInput') {
    return compactStrings([song.title, song.artist, song.album]);
  }
  return compactStrings([song.title, song.artist, song.album, song.displayTitle, song.displayArtist]);
}

export const normalizeSongSearchTerm = (term: string): string => term.trim().toLowerCase();

/** Whether one of the row's IDs (song ID, song input ID, ISRC) contains the normalized term. */
export function matchesSongSearchId(song: SearchableContentItem, normalizedTerm: string): boolean {
  return normalizedTerm !== '' && getSongSearchIds(song).some((id) => id.toLowerCase().includes(normalizedTerm));
}

/**
 * "Contains" match on the row's IDs (song ID, song input ID, ISRC) and on its title / artist / album /
 * display fields, so a partial ID copied from a log or a spreadsheet cell is enough to find the row.
 */
export function matchesSongSearch(song: SearchableContentItem, normalizedTerm: string): boolean {
  if (!normalizedTerm) {
    return true;
  }
  if (matchesSongSearchId(song, normalizedTerm)) {
    return true;
  }
  return getSongSearchTexts(song).some((text) => text.toLowerCase().includes(normalizedTerm));
}
